import React, { useRef, useEffect } from 'react';
import { AnimationData, AnimationTargets } from '../types';
import { Language } from '../data/translations';
import Card from './Card';

const FlyingCard: React.FC<{
    animation: AnimationData;
    onEnd: (id: string) => void;
    targets: AnimationTargets;
    language: Language;
}> = ({ animation, onEnd, targets, language }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const getEndStyle = (): React.CSSProperties => {
        if (animation.type === 'to-effect') {
            const isSelf = animation.playerId === targets.localPlayerId;
            const targetRect = isSelf ? targets.playerAvatar : targets.aiAvatar;
            if (!targetRect) return { opacity: 0 };

            const yOffset = isSelf ? -50 : 50;

            return {
                top: `${targetRect.top + (targetRect.height / 2) + yOffset}px`,
                left: `${targetRect.left + targetRect.width / 2 - 40}px`,
                transform: 'scale(0.6) rotate(360deg)',
                opacity: 1,
            };
        } else { // from-effect
            const targetRect = targets.deckZone;
            if (!targetRect) return { opacity: 0 };
            return {
                top: `${targetRect.top}px`,
                left: `${targetRect.right - 40}px`,
                transform: 'scale(0.5) rotate(-360deg) translateX(30vw)',
                opacity: 0,
            };
        }
    };

    const startStyle: React.CSSProperties = {
        position: 'fixed',
        top: `${animation.startRect.top}px`,
        left: `${animation.startRect.left}px`,
        transition: 'none',
        zIndex: 100,
        transform: animation.type === 'from-effect' ? 'scale(0.6)' : 'scale(1)',
    };

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const end = getEndStyle();
        const startTop = animation.startRect.top;
        const startLeft = animation.startRect.left;
        const endTop = Number(String(end.top || '').replace('px', '')) || startTop;
        const endLeft = Number(String(end.left || '').replace('px', '')) || startLeft;
        const dx = endLeft - startLeft;
        const dy = endTop - startTop;
        const startScale = animation.type === 'from-effect' ? 0.6 : 1;
        const endScale = end.transform?.includes('scale(0.6)') ? 0.6 : (end.transform?.includes('scale(0.5)') ? 0.5 : 1);
        const rotateDeg = animation.type === 'to-effect' ? 360 : -360;
        const anim = el.animate([
            { transform: `translate(0px, 0px) scale(${startScale}) rotate(0deg)`, opacity: 1 },
            { transform: `translate(${dx}px, ${dy}px) scale(${endScale}) rotate(${rotateDeg}deg)`, opacity: end.opacity ?? 1 }
        ], { duration: 800, easing: 'ease-in-out' });
        anim.onfinish = () => onEnd(animation.id);
        return () => { anim.cancel(); };
    }, []);

    return (
        <div ref={ref} style={startStyle}>
            <Card card={animation.card} language={language} />
        </div>
    );
};

export default FlyingCard;
